import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { supabase } from '@/shared/lib/supabase'

export type SupportedLocale = 'en' | 'fr'

export const SUPPORTED_LOCALES: { code: SupportedLocale; name: string; nativeName: string }[] = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'fr', name: 'French', nativeName: 'Français' },
]

async function updateLocale(userId: string, locale: SupportedLocale): Promise<void> {
  const { error } = await supabase
    .from('users')
    .update({
      locale,
      updated_at: new Date().toISOString(),
    })
    .eq('id', userId)

  if (error) {
    throw new Error(error.message)
  }
}

export function useUpdateLocale(userId: string) {
  const queryClient = useQueryClient()
  const { i18n } = useTranslation()

  return useMutation({
    mutationFn: (locale: SupportedLocale) => updateLocale(userId, locale),
    onMutate: async (locale) => {
      // Optimistically change the language immediately
      await i18n.changeLanguage(locale)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['currentUser'] })
    },
    onError: () => {
      // On error, the UI will show the new language but DB wasn't updated
      // User can retry or refresh to get the correct state
    },
  })
}
